import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import type { DatesSetArg } from '@fullcalendar/core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loading, SearchBar } from 'components';
import { Text } from 'components/typography';
import { Icon, Modal, Multiselect, Tooltip } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import useUserLocale from 'hooks/useUserLocale';
import UserDataContext from 'lane-shared/contexts/UserDataContext';
import { FullCalendarEvent } from '../hooks/useReservableCalendarView';
import { useReservablesOnChannel } from '../hooks/useReservablesOnChannel';
import { useUnavailableRangesForContents } from '../hooks/useUnavailableRangesForContents';
import { useTags } from '../hooks/useTags';
import { EventDetails } from '../Calendar/EventDetails';
import { DayGridCalendar } from './DayGridCalendar';
import { DayModal } from './DayModal';
import { mapReservablesToMonthEvents } from './mapReservablesToMonthEvents';
import { filterMonthEventsByTagsAndSearch } from './filterMonthEventsByTagsAndSearch';
import { useCalendarModals } from './useCalendarModals';
import styles from './index.scss';

// The reservables query has no cursor var, so we load a single large fixed page.
export const MONTH_VIEW_PAGE_SIZE = 1000;

// Sibling tab under `/reservable-management/:view`; resolved relative to the
// current calendar route, matching the TabStrip navigation idiom. The List view
// reads `search`/`startDate`/`endDate` from the query string
// (useReservableTableViewQueryParams), so a grouped row deep-links straight to
// its filtered entries.
const LIST_VIEW_PATH = 'list';

const TRANSLATION_KEYS = {
  bookingsError:
    'web.admin.channel.reservableManagement.calendar.fetchBookings.error',
  noResults: 'web.admin.channel.reservableManagement.tabs.noResults',
  tagLabel: 'web.admin.channel.reservableManagement.calendar.tagLabel',
  searchLabel: 'web.admin.channel.reservableManagement.calendar.searchLabel',
  searchTooltip:
    'web.admin.channel.reservableManagement.calendar.searchPlaceholder',
  selectOption: 'web.components.dropdown.selectOption',
};

type Props = {
  channelId: string;
  channelTimeZone: string;
};

type MultiFilterType = { label: string; value: string };

type Range = { start: Date; end: Date };

function getInitialMonthRange(channelTimeZone: string): Range {
  const now = DateTime.now().setZone(channelTimeZone);

  return {
    start: now.startOf('month').toJSDate(),
    end: now.endOf('month').toJSDate(),
  };
}

export function MonthCalendar({ channelId, channelTimeZone }: Props) {
  const { t } = useTranslation();
  const userLocale = useUserLocale();
  const history = useHistory();
  const { user } = useContext(UserDataContext);

  const [range, setRange] = useState<Range>(() =>
    getInitialMonthRange(channelTimeZone)
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<MultiFilterType[]>([]);

  const { tags } = useTags({ channelId });

  const {
    reservablesOnChannel,
    fetchReservablesOnChannel,
    isInitialLoad,
    error: reservablesFetchError,
  } = useReservablesOnChannel({
    channelId,
    pageSize: MONTH_VIEW_PAGE_SIZE,
    rangeStart: range.start,
    rangeEnd: range.end,
    includeCancelled: true,
  });

  const { reservablesWithUnavailableRanges, error: unavailablesFetchError } =
    useUnavailableRangesForContents({
      referenceDate: range.start,
      reservablesOnChannel,
      user,
    });

  const events = useMemo(
    () =>
      mapReservablesToMonthEvents(reservablesWithUnavailableRanges, {
        channelTimeZone,
        locale: userLocale,
        t,
      }),
    [reservablesWithUnavailableRanges, channelTimeZone, userLocale, t]
  );

  const filteredEvents = useMemo(
    () =>
      filterMonthEventsByTagsAndSearch(events, {
        selectedTags,
        searchTerm,
      }),
    [events, selectedTags, searchTerm]
  );

  const {
    selectedEvent,
    dayModal,
    handleEventClick,
    handleMoreLinkClick,
    closeDayModal,
    closeSelectedEvent,
    selectEventFromDay,
  } = useCalendarModals(filteredEvents, channelTimeZone);

  // Grouped rows ("N entries") stand in for several bookings sharing one time
  // slot, so there is no single detail to open; deep-link to the List view
  // filtered to that reservable + exact time window instead. Single-entry rows
  // keep opening the detail modal (selectEventFromDay).
  const navigateToFilteredListView = (event: FullCalendarEvent) => {
    const params = new URLSearchParams({
      search: event.extendedProps.reservableName ?? '',
      startDate: event.start.toISOString(),
      endDate: event.end.toISOString(),
    });

    closeDayModal();
    history.push(`${LIST_VIEW_PATH}?${params.toString()}`);
  };

  const noResults = useMemo(
    () =>
      filteredEvents.length === 0 &&
      (searchTerm.length !== 0 || selectedTags.length !== 0),
    [filteredEvents, searchTerm, selectedTags]
  );

  useEffect(() => {
    if (noResults) {
      window.Toast.show(<Text>{t(TRANSLATION_KEYS.noResults)}</Text>);
    }
  }, [noResults, t]);

  // hasNextPage isn't fetched by the query, so detect page-size saturation via
  // the count and warn; otherwise dropped reservables are invisible.
  useEffect(() => {
    if (reservablesOnChannel.length >= MONTH_VIEW_PAGE_SIZE) {
      console.warn(
        `[MonthCalendar] reservable count reached the ${MONTH_VIEW_PAGE_SIZE} page-size cap; ` +
          'some reservables may be missing from the month view (D4 / Open Item 11.2).'
      );
    }
  }, [reservablesOnChannel.length]);

  const error = reservablesFetchError || unavailablesFetchError;

  useEffect(() => {
    if (error) {
      window.Toast.show(<Text>{t(TRANSLATION_KEYS.bookingsError)}</Text>);
    }
  }, [error, t]);

  // Dedupe on unchanged range to avoid a double-fetch (nav routes through datesSet).
  const handleDatesSet = (arg: DatesSetArg) => {
    setRange(prev =>
      prev.start.getTime() === arg.start.getTime() &&
      prev.end.getTime() === arg.end.getTime()
        ? prev
        : { start: arg.start, end: arg.end }
    );
  };

  return (
    <div className={styles.wrapper}>
      {isInitialLoad && <Loading />}
      <div className={styles.calendarArea}>
        <DayGridCalendar
          events={filteredEvents}
          channelTimeZone={channelTimeZone}
          headerActions={
            <>
              <div className={styles.filter}>
                <label className={styles.filterLabel} data-test="search-label">
                  {t(TRANSLATION_KEYS.searchLabel)}
                  <Tooltip
                    placement="top"
                    TooltipComponent={t(TRANSLATION_KEYS.searchTooltip)}
                  >
                    <Icon
                      name="info-circle"
                      set={ICON_SET_FONTAWESOME}
                      type="far"
                      className={styles.searchInfoIcon}
                      testId="month-calendar-search-info"
                    />
                  </Tooltip>
                </label>
                <SearchBar
                  searchOptions={{ search: searchTerm }}
                  onSearchChange={value => setSearchTerm(value)}
                  searchInputPlaceholder=" "
                  testId="month-calendar-search"
                />
              </div>
              <div className={styles.filter}>
                <label className={styles.filterLabel} data-test="tag-label">
                  {t(TRANSLATION_KEYS.tagLabel)}
                </label>
                <Multiselect
                  items={tags}
                  onChange={setSelectedTags}
                  value={selectedTags}
                  doTranslation={false}
                  truncateSelectedItems
                  placeholder={t(TRANSLATION_KEYS.selectOption)}
                  data-test="month-calendar-tag-selector"
                />
              </div>
            </>
          }
          onDatesSet={handleDatesSet}
          eventClick={handleEventClick}
          onMoreLinkClick={handleMoreLinkClick}
        />
      </div>
      {dayModal && (
        <DayModal
          date={dayModal.date}
          events={dayModal.events}
          locale={userLocale}
          anchorRect={dayModal.anchorRect}
          onClose={closeDayModal}
          onSelectEvent={selectEventFromDay}
          onSelectGroupedEvent={navigateToFilteredListView}
        />
      )}
      {selectedEvent && (
        <Modal isOpen includeHeader={false} onClose={closeSelectedEvent}>
          <EventDetails
            event={selectedEvent}
            userLocale={userLocale}
            onStatusUpdate={fetchReservablesOnChannel}
            onClose={closeSelectedEvent}
          />
        </Modal>
      )}
    </div>
  );
}
